import { useState, useMemo, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { savePendingAllocations } from '../../../../redux/globalSlices/cashApplicationSlice';
import { openMessageModal, closeMessageModal } from '../../../../redux/globalSlices/genericSlice';
import {
  approveDepositGroupThunk,
  approveOneClickThunk,
  postUnappliedThunk,
  unlinkBankTransactionThunk,
  updateRemittanceAllocationsThunk,
} from '../../../../redux/services/cashApplicationService';
import type { AppliedInvoice, BankTransactionRecord, RemittanceRecord } from '@dill/dill-shared';
import {
  calculateUnappliedAmount,
  depositUnaccountedAmount,
  describeUnapprovedOpenCredit,
  appDelay,
} from '@dill/dill-shared';
import { ConfidenceBadge } from '../components/ConfidenceBadge';
import { ChannelBadge } from '../components/ChannelBadge';
import { MatchedInvoicesTooltip } from '../components/MatchedInvoicesTooltip';
import { DepositGroupBand } from '../components/DepositGroupBand';
import { AdjustAllocationModal } from './AdjustAllocationModal';
import { DillButton } from '../../../general/Dill';
import {
  CaretDown,
  CheckCircle,
  LinkBreak,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  Warning,
} from 'phosphor-react';
import { formatCurrency, formatDate, formatDateTime } from '../utils';
import { openDillToast } from '../../../../utils/helpers';
import { useDeleteRemittance } from './useDeleteRemittance';

/**
 * The payments one bank credit confirms, and what the credit still owes.
 *
 * Every payment in this queue is in one of these. A record only reaches 1-Click Review once it is
 * SETTLED, and settling is what links it to a credit — so "grouped by deposit" is not a special
 * case here, it is the shape of the whole queue, and there is nothing left for a flat table to
 * hold. Two bands stand outside that: a supplier with no bank feed, whose payments share no credit,
 * and the inconsistent case below.
 */
interface DepositGroup {
  key: string;
  deposit: BankTransactionRecord | null;
  /** Set when there is no credit to name — see the schema note on `settlementBankRecordId`. */
  title?: string;
  records: RemittanceRecord[];
  total: number;
  unaccounted: number;
  /** Payers with no Dill customer, which is what makes the group unpostable. */
  unpostable: RemittanceRecord[];
  /**
   * Members leaving a remainder nobody has agreed to post as open credit.
   *
   * Approving is per credit and posts every member, so one short allocation nobody looked at would
   * otherwise become open credit in the ERP on the strength of a button pressed for the rows above
   * it — and the band's Approve sits in the header, reachable while the rows are collapsed.
   */
  unapprovedOpenCredit: RemittanceRecord[];
  /**
   * Payments on the same credit that are sitting in Exceptions.
   *
   * Shown here even though they belong to another tab, because approving is per credit and not per
   * queue: these post too. Leaving them off screen is what let an operator approve what looked like
   * two payments and send a third, unallocated one to the ERP as unapplied cash.
   */
  exceptions: RemittanceRecord[];
}

export const OneClickReviewQueue = () => {
  const dispatch = useAppDispatch();
  const items = useAppSelector((state) => state.cashApplication.oneClickReviewItems);
  // Another tab's rows, read here only to say which credits are not approvable yet.
  const exceptionItems = useAppSelector((state) => state.cashApplication.exceptionItems);
  const openInvoices = useAppSelector((state) => state.cashApplication.openInvoices);
  const pendingAllocations = useAppSelector((state) => state.cashApplication.pendingAllocations);
  const bankTransactions = useAppSelector((state) => state.cashApplication.bankTransactions);
  const loading = useAppSelector((state) => state.cashApplication.loadingList.length > 0);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const supplierId = currentSupplier?.id;

  const [search, setSearch] = useState('');
  /**
   * Which bands the operator has closed, rather than which are open.
   *
   * Open is the default and has to survive a refetch: the queue re-fetches after every approval, so
   * tracking the open set would reopen everything the operator had tidied away — or, worse, close
   * everything, because a newly arrived group would not be in it.
   */
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());

  const toggleGroup = useCallback((key: string) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Adjustment modal state
  const [adjustItem, setAdjustItem] = useState<RemittanceRecord | null>(null);
  const [allocations, setAllocations] = useState<Record<string, number>>({});
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [savingAllocation, setSavingAllocation] = useState(false);
  // Reset whenever the modal opens: consent to place a remainder belongs to one payment, not to
  // the next record the operator happens to open.
  const [postRemainderAsCredit, setPostRemainderAsCredit] = useState(false);

  const confirmDelete = useDeleteRemittance(supplierId);

  /**
   * Break the link between this payment and the deposit vouching for it.
   *
   * The right answer when a reviewer disagrees with the match itself rather than the allocation:
   * unconfirmed, the payment returns to Awaiting Bank, where the deposit that really paid it can
   * be found. This replaced "Move to Exceptions", which changed a record's status to say the
   * matcher was wrong while leaving the wrong deposit attached to it.
   */
  const handleUnlink = useCallback(
    (record: RemittanceRecord) => {
      const bankTransactionId = record.settlementBankRecordId;
      if (!bankTransactionId) {
        return;
      }
      const payer = record.payerName ?? 'Unknown';

      dispatch(
        openMessageModal({
          buttonText: 'Cancel',
          isSecondButton: true,
          secondButtonText: 'Unlink deposit',
          modalType: 'WARN',
          title: 'Unlink this deposit?',
          subTitle:
            `The payment from ${payer} will no longer be confirmed by this deposit and moves ` +
            `back to Awaiting Bank. The deposit returns to Unmatched Deposits so it can be ` +
            `linked to the right payment.`,
          onSecondButtonClick: async () => {
            if (!supplierId) return;
            await appDelay(10);
            dispatch(
              openMessageModal({
                buttonText: '',
                modalType: 'LOADING',
                title: 'Unlinking deposit',
                subTitle: `Detaching the payment from ${payer}...`,
              })
            );
            const result = await dispatch(
              unlinkBankTransactionThunk({
                supplierId,
                bankTransactionId,
                // This payment only — a bulk credit confirms several, and the reviewer is
                // disputing the one in front of them. See the same note in AdjustAllocationModal.
                remittanceId: record.id,
              })
            );
            dispatch(closeMessageModal());

            if (unlinkBankTransactionThunk.rejected.match(result)) {
              openDillToast({
                message: (result.payload as string) ?? 'Failed to unlink the deposit.',
                type: 'ERROR',
                autoClose: 5000,
              });
              return;
            }

            const { stillLinked } = result.payload;
            openDillToast({
              message:
                stillLinked.length > 0
                  ? `Deposit unlinked from ${payer}. It still confirms ${stillLinked.length} other payment(s).`
                  : `Deposit unlinked. The payment from ${payer} is awaiting confirmation again.`,
              type: 'SUCCESS',
            });
          },
        })
      );
    },
    [dispatch, supplierId]
  );

  const openAdjustModal = useCallback(
    (record: RemittanceRecord) => {
      setAdjustItem(record);
      const pending = pendingAllocations[record.id];
      if (pending && pending.length > 0) {
        const allocs: Record<string, number> = {};
        pending.forEach((inv) => {
          allocs[inv.invoiceId] = inv.amountApplied;
        });
        setAllocations(allocs);
      } else {
        const initialAllocs: Record<string, number> = {};
        record.matchedInvoices.forEach((inv) => {
          initialAllocs[inv.invoiceId] = inv.suggestedAmount ?? inv.openBalance;
        });
        setAllocations(initialAllocs);
      }
      setInvoiceSearch('');
      // Reopened on an approval already given, the box is already ticked — it is a decision on the
      // record, not a question asked afresh each time the modal opens.
      setPostRemainderAsCredit(record.openCreditApproved === true);
    },
    [pendingAllocations]
  );

  const closeAdjustModal = () => {
    setAdjustItem(null);
    setAllocations({});
    setInvoiceSearch('');
    setPostRemainderAsCredit(false);
  };

  const handleAllocationChange = (invoiceId: string, value: string) => {
    const num = parseFloat(value) || 0;
    setAllocations((prev) => ({ ...prev, [invoiceId]: num }));
  };

  const buildAppliedInvoices = (): AppliedInvoice[] =>
    Object.entries(allocations)
      .filter(([, amount]) => amount > 0)
      .map(([invoiceId, amount]) => {
        const inv = openInvoices.find((i) => i.id === invoiceId);
        return { invoiceId, docNumber: inv?.docNumber || '', amountApplied: amount };
      });

  const totalAllocated = useMemo(
    () => Object.values(allocations).reduce((sum, val) => sum + (val || 0), 0),
    [allocations]
  );

  const remaining = adjustItem ? adjustItem.amount - totalAllocated : 0;

  /**
   * Posting requires the payment to be fully accounted for — not fully allocated.
   *
   * A $32,384.82 cheque allocated across four invoices totalling $32,383.18 leaves $1.64 that no
   * invoice claims. Requiring an exact allocation blocked that entirely, so the only ways out were
   * to inflate an allocation past an invoice's balance or to abandon the allocation and post the
   * whole payment unapplied. Accounting for the remainder as open credit is the honest third
   * option, and the backend already records the split on the receipt.
   */
  const isFullyAllocated = Math.abs(remaining) < 0.01;
  // An allocation of nothing is accounted for too once the operator has signed the whole payment
  // over to open credit — same post, same approval, just no invoice on it.
  const isValidAllocation =
    !!adjustItem &&
    (totalAllocated > 0 || postRemainderAsCredit) &&
    remaining > -0.01 &&
    (isFullyAllocated || postRemainderAsCredit);
  // Saving an approval with nothing allocated is a real decision — it is how a member of a bulk
  // credit, whose modal offers no post button, says its payment goes on account.
  const hasAllocations = totalAllocated > 0 || postRemainderAsCredit;

  const filteredOpenInvoices = useMemo(() => {
    if (!adjustItem) return [];
    const customerInvoices = openInvoices.filter(
      (inv) =>
        adjustItem.matchedInvoices.map((ii) => ii.invoiceId).includes(inv.id) ||
        !adjustItem?.buyerId
    );

    if (!invoiceSearch) return customerInvoices;
    // Named `query` rather than `search`: this is the adjust modal's invoice filter, and the
    // queue's own payment search now holds that name.
    const query = invoiceSearch.toLowerCase();
    return customerInvoices.filter(
      (inv) =>
        inv.docNumber?.toLowerCase().includes(query) ||
        inv.customerName?.toLowerCase().includes(query) ||
        inv.poNumber?.toLowerCase().includes(query)
    );
  }, [adjustItem, invoiceSearch, openInvoices]);

  const handleAdjustAndPost = async () => {
    if (!adjustItem || !supplierId) return;
    const appliedInvoices = buildAppliedInvoices();
    dispatch(
      openMessageModal({
        buttonText: '',
        modalType: 'LOADING',
        title: 'Posting payment',
        subTitle:
          appliedInvoices.length === 0
            ? 'Posting the whole payment as open credit to ERP...'
            : `Applying to ${appliedInvoices.length} invoice(s) and posting to ERP...`,
      })
    );
    const result = await dispatch(
      approveOneClickThunk({
        supplierId,
        remittanceId: adjustItem.id,
        allocations: appliedInvoices,
        postRemainderAsCredit,
      })
    );
    dispatch(closeMessageModal());
    closeAdjustModal();
    if (result.meta.requestStatus === 'fulfilled') {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'SUCCESS',
          title: 'Payment posted',
          subTitle:
            appliedInvoices.length === 0
              ? `Payment from ${adjustItem.payerName} (${formatCurrency(
                  adjustItem.amount
                )}) posted in full as open credit on the account.`
              : `Payment from ${adjustItem.payerName} (${formatCurrency(
                  adjustItem.amount
                )}) applied to ${appliedInvoices.length} invoice(s) and posted.` +
                (remaining > 0.01
                  ? ` ${formatCurrency(remaining)} was posted as open credit on the account.`
                  : ''),
        })
      );
    } else {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Posting failed',
          subTitle: `Failed to apply and post payment. ${
            (result.payload as string) ?? 'Please try again.'
          }`,
        })
      );
    }
  };

  /**
   * Save the allocation to the record, then keep it in `pendingAllocations` too.
   *
   * It used to do only the second half, which made "You can approve later" untrue of any later
   * than this tab: `pendingAllocations` is browser state and the store is not persisted, so a
   * reload dropped the edit and the record came back carrying the matcher's original suggestion.
   * The other two queues have always written through to the record; this is the same call.
   *
   * The local copy stays because it is what `approveDepositGroupThunk` sends as the per-member
   * override — now agreeing with what is on the record rather than standing in for it.
   */
  const handleSaveOnly = async () => {
    if (!adjustItem || !supplierId) return;
    const appliedInvoices = buildAppliedInvoices();
    setSavingAllocation(true);
    try {
      const result = await dispatch(
        updateRemittanceAllocationsThunk({
          supplierId,
          remittanceId: adjustItem.id,
          allocations: appliedInvoices.map((applied) => {
            const invoice = openInvoices.find((candidate) => candidate.id === applied.invoiceId);
            const matched = adjustItem.matchedInvoices.find(
              (candidate) => candidate.invoiceId === applied.invoiceId
            );
            return {
              invoiceId: applied.invoiceId,
              docNumber: applied.docNumber || matched?.docNumber || '',
              amount: applied.amountApplied,
              invoiceAmount: invoice?.amount ?? matched?.invoiceAmount ?? null,
              openBalance: invoice?.balance ?? matched?.openBalance ?? null,
            };
          }),
          postRemainderAsCredit,
        })
      );

      if (result.meta.requestStatus !== 'fulfilled') {
        // Left open, with the edit intact: the operator's work is still on screen to retry or copy.
        openDillToast({
          message: (result.payload as string) ?? 'Failed to save the allocation.',
          type: 'ERROR',
        });
        return;
      }

      dispatch(savePendingAllocations({ id: adjustItem.id, allocations: appliedInvoices }));
      closeAdjustModal();
      openDillToast({
        message: 'Allocation changes saved. You can approve later.',
        type: 'SUCCESS',
      });
    } finally {
      setSavingAllocation(false);
    }
  };

  const handlePostUnapplied = async () => {
    if (!adjustItem || !supplierId) return;
    dispatch(
      openMessageModal({
        buttonText: '',
        modalType: 'LOADING',
        title: 'Posting unapplied cash',
        subTitle: 'Posting payment as unapplied cash to ERP...',
      })
    );
    const result = await dispatch(
      postUnappliedThunk({
        supplierId,
        remittanceId: adjustItem.id,
        postMethod: 'ONE_CLICK_REVIEW',
      })
    );
    dispatch(closeMessageModal());
    closeAdjustModal();
    if (result.meta.requestStatus === 'fulfilled') {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'SUCCESS',
          title: 'Payment posted',
          subTitle: `Payment from ${adjustItem.payerName} (${formatCurrency(
            adjustItem.amount
          )}) posted as unapplied cash.`,
        })
      );
    } else {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Posting failed',
          subTitle: `Failed to post unapplied cash. ${
            (result.payload as string) ?? 'Please try again.'
          }`,
        })
      );
    }
  };

  /**
   * The whole queue, by the credit each payment arrived in.
   *
   * Keyed on `settlementBankRecordId` — the credit the record was settled against, and the same key
   * the ERP deposit is made under, so what the operator approves as one thing is exactly what
   * becomes one deposit.
   *
   * Every payment lands in a group, including a credit that brought in only one. There is no
   * "ungrouped" case to fall back to: a record reaches this queue only once it is SETTLED, and
   * settling is what links it to a credit — so "grouped by deposit" is the shape of the queue rather
   * than a special case. A record that claims to be settled but names no credit should not exist;
   * it is collected under a heading that says so rather than being dropped, because a payment that
   * silently disappears from every queue is worse than one that is visibly wrong.
   */
  /**
   * The allocation a post would actually carry: the operator's unsaved edit when there is one, the
   * record's own matches otherwise — the same precedence `handleApproveGroup` sends.
   *
   * One derivation, because the row's open-credit warning and the band's refusal to approve have to
   * agree about the remainder. Two of them drift, and the visible symptom is a disabled button with
   * nothing on screen explaining it.
   */
  const allocationForPost = useCallback(
    (record: RemittanceRecord): { amountApplied: number }[] => {
      // `length > 0`, matching what `handleApproveGroup` treats as an override — an empty pending
      // set is not sent, so reading it as "allocated nothing" would block on a remainder the
      // server would never see.
      const pending = pendingAllocations[record.id];
      return pending && pending.length > 0
        ? pending
        : record.matchedInvoices.map((invoice) => ({
            amountApplied: invoice.suggestedAmount ?? 0,
          }));
    },
    [pendingAllocations]
  );

  const depositGroups = useMemo(() => {
    const byDeposit = new Map<string, RemittanceRecord[]>();
    const unlinked: RemittanceRecord[] = [];
    const noFeed: RemittanceRecord[] = [];

    for (const item of items) {
      // No bank feed at all, so there is no credit to wait for and never will be. These post one
      // receipt each — they share nothing that could group them — and they reach this queue rather
      // than Awaiting Bank precisely because nothing there could ever move them on.
      if (item.settlementStatus === 'NOT_APPLICABLE') {
        noFeed.push(item);
        continue;
      }
      const key = item.settlementBankRecordId;
      if (!key) {
        unlinked.push(item);
        continue;
      }
      byDeposit.set(key, [...(byDeposit.get(key) ?? []), item]);
    }

    // The other tab's payments, by the credit they are linked to. Same key, so a credit's
    // unreviewed members are found without walking the whole list per group.
    const exceptionsByDeposit = new Map<string, RemittanceRecord[]>();
    for (const item of exceptionItems) {
      const key = item.settlementBankRecordId;
      if (!key) {
        continue;
      }
      exceptionsByDeposit.set(key, [...(exceptionsByDeposit.get(key) ?? []), item]);
    }

    const groups: DepositGroup[] = [];
    for (const [key, records] of byDeposit) {
      const deposit = bankTransactions.find((txn) => txn.id === key) ?? null;
      groups.push({
        key,
        deposit,
        records,
        total: records.reduce((sum, record) => sum + record.amount, 0),
        // Read off the credit rather than compared against the members' total: a payment already
        // posted has left this queue, so the members on screen are not the whole of what the credit
        // accounts for. `matchedAmount` is.
        unaccounted: deposit ? depositUnaccountedAmount(deposit) : 0,
        unpostable: records.filter((record) => !record.buyerId),
        unapprovedOpenCredit: records.filter(
          (record) => !!describeUnapprovedOpenCredit(record, allocationForPost(record))
        ),
        exceptions: exceptionsByDeposit.get(key) ?? [],
      });
    }

    // Newest credit first. A credit whose record has not loaded sorts last rather than first, so a
    // missing date cannot push an unidentifiable band to the top of the queue.
    groups.sort((a, b) => (b.deposit?.date ?? '').localeCompare(a.deposit?.date ?? ''));

    /**
     * The supplier has no bank feed, so nothing will ever confirm these and they are postable as
     * they stand. Grouped only to keep one layout on the screen — they share no credit, so
     * approving the band posts each on its own receipt.
     */
    if (noFeed.length > 0) {
      groups.push({
        key: 'no-bank-feed',
        deposit: null,
        title: 'No bank feed connected',
        records: noFeed,
        total: noFeed.reduce((sum, record) => sum + record.amount, 0),
        unaccounted: 0,
        unpostable: noFeed.filter((record) => !record.buyerId),
        unapprovedOpenCredit: noFeed.filter(
          (record) => !!describeUnapprovedOpenCredit(record, allocationForPost(record))
        ),
        // These share no credit, so there is no group approval that could sweep up a neighbour.
        exceptions: [],
      });
    }

    if (unlinked.length > 0) {
      /**
       * Last, and honestly labelled. `settleRecord` writes the credit id and the SETTLED status
       * together, so a record here means those two writes came apart — there is no deposit to name
       * and no total to check against, and `unaccounted` stays 0 rather than claiming a shortfall
       * the band cannot know about.
       *
       * Not approvable: the group endpoint is keyed by bank credit and there is none, so the band
       * says to re-link them rather than offering a button that cannot work.
       */
      groups.push({
        key: 'no-linked-deposit',
        deposit: null,
        title: 'No bank deposit recorded',
        records: unlinked,
        total: unlinked.reduce((sum, record) => sum + record.amount, 0),
        unaccounted: 0,
        unpostable: unlinked.filter((record) => !record.buyerId),
        unapprovedOpenCredit: unlinked.filter(
          (record) => !!describeUnapprovedOpenCredit(record, allocationForPost(record))
        ),
        // Named no credit, so nothing can be grouped with them. The band is unapprovable anyway.
        exceptions: [],
      });
    }

    return groups;
  }, [items, exceptionItems, bankTransactions, allocationForPost]);

  /**
   * Whether the payment open in the adjust modal shares its credit with others.
   *
   * This is what decides whether the modal offers to post. A credit with one payment can be posted
   * from there safely — that payment is the whole of the credit, so the deposit is makeable the
   * moment it posts. A credit with several cannot: posting one member would strand the rest against
   * a deposit that cannot be made until they catch up.
   */
  const adjustItemIsBulkMember = useMemo(
    () =>
      !!adjustItem &&
      depositGroups.some(
        (group) =>
          // `deposit !== null` matters: the two sentinel bands are collections, not credits. Three
          // no-feed payments under one heading share nothing, and treating them as one deposit's
          // members would strip Post from the modal and leave that supplier unable to post at all.
          group.deposit !== null &&
          group.records.length > 1 &&
          group.records.some((record) => record.id === adjustItem.id)
      ),
    [adjustItem, depositGroups]
  );

  /**
   * The bands on screen, and which of each band's rows the search left showing.
   *
   * The group is carried whole and never narrowed, because search finds a payment rather than
   * choosing one: approving is per credit and posts everything linked to it, so a band has to mean
   * the same thing with the box full as with it empty. A filtered group reaching the handlers is
   * what dropped a saved allocation on a hidden row back to Dill's suggestion at post time.
   * `visibleRecords` renders rows and nothing else.
   */
  const isSearching = search.trim().length > 0;

  const visibleGroups = useMemo(() => {
    // Leading "#" dropped and runs of whitespace flattened, on both sides. The rows print a check
    // as "Check #1234", so typing it back verbatim found nothing, and a payer name carrying a
    // double space in the advice could not be typed at all.
    const normalize = (value: string) => value.replace(/\s+/g, ' ').trim().toLowerCase();
    const query = normalize(search.replace(/^\s*#/, ''));
    if (!query) {
      return depositGroups.map((group) => ({ group, visibleRecords: group.records }));
    }
    // Filtered by member, not by band: a search for one payer should leave its deposit on screen
    // showing that payer, rather than hiding the credit the payment belongs to.
    const matches = (record: RemittanceRecord) =>
      normalize(`${record.payerName ?? ''} ${record.checkNumber ?? ''}`).includes(query);

    return (
      depositGroups
        .map((group) => ({ group, visibleRecords: group.records.filter(matches) }))
        // A band is kept when the search hits any of its payments, unreviewed ones included. The
        // exceptions themselves are never filtered out of the band: they are what blocks Approve,
        // so hiding one would leave a disabled button with nothing on screen explaining it.
        .filter(
          ({ group, visibleRecords }) => visibleRecords.length > 0 || group.exceptions.some(matches)
        )
    );
  }, [depositGroups, search]);

  /**
   * Post a whole bank deposit as one receipt, carrying whatever allocations the operator saved.
   *
   * One request and one receipt, not one per payment: this is a single decision by a single person,
   * and the ERP deposit that reconciles it is a single document. The write is a transaction, so
   * either every payment on the credit is claimed or none is — there is no partial outcome here,
   * only a partial *post*, which happens later in the ERP and shows up as PARTIAL in Post History.
   */
  const handleApproveGroup = useCallback(
    async (group: DepositGroup) => {
      if (!supplierId) return;

      const edited: Record<string, AppliedInvoice[]> = {};
      group.records.forEach((record) => {
        const pending = pendingAllocations[record.id];
        if (pending && pending.length > 0) {
          edited[record.id] = pending;
        }
      });

      dispatch(
        openMessageModal({
          buttonText: '',
          modalType: 'LOADING',
          title: 'Posting deposit',
          subTitle: `Applying ${group.records.length} payment(s) and posting one deposit to the ERP...`,
        })
      );
      const result = await dispatch(
        approveDepositGroupThunk({
          supplierId,
          bankTransactionId: group.key,
          allocations: Object.keys(edited).length > 0 ? edited : undefined,
        })
      );
      dispatch(closeMessageModal());

      if (result.meta.requestStatus === 'fulfilled') {
        const { posted } = result.payload as { posted: number };
        dispatch(
          openMessageModal({
            buttonText: 'Close',
            modalType: 'SUCCESS',
            title: 'Deposit posted',
            subTitle:
              `${posted} payment(s) totalling ${formatCurrency(group.total)} approved on one ` +
              'receipt. One deposit will be created for the whole bank credit. Follow it in Post ' +
              'History — a payment the ERP refuses can be retried on its own from there.',
          })
        );
      } else {
        dispatch(
          openMessageModal({
            buttonText: 'Close',
            modalType: 'ERROR',
            title: 'Posting failed',
            subTitle: `${
              (result.payload as string) ?? 'Please try again.'
            } Nothing on this deposit was posted.`,
          })
        );
      }
    },
    [dispatch, supplierId, pendingAllocations]
  );

  /**
   * Post a collection of payments that share no bank credit, one receipt each.
   *
   * Only the no-bank-feed band. Sequential rather than parallel so a failure stops rather than
   * fanning out, and so the message can say how far it got.
   */
  const handleApproveEach = useCallback(
    async (group: DepositGroup) => {
      if (!supplierId) return;

      dispatch(
        openMessageModal({
          buttonText: '',
          modalType: 'LOADING',
          title: 'Posting payments',
          subTitle: `Posting ${group.records.length} payment(s) to the ERP...`,
        })
      );

      let posted = 0;
      let failure: string | null = null;
      for (const record of group.records) {
        const pending = pendingAllocations[record.id];
        const edited = pending && pending.length > 0 ? pending : undefined;
        // Nothing allocated is a different operation: the money lands on the customer's account as
        // open credit for someone to apply later, which is the endpoint built for it.
        const unallocated = (edited ?? record.matchedInvoices).length === 0;
        const result = await dispatch(
          unallocated
            ? postUnappliedThunk({
                supplierId,
                remittanceId: record.id,
                postMethod: 'ONE_CLICK_REVIEW',
              })
            : approveOneClickThunk({ supplierId, remittanceId: record.id, allocations: edited })
        );
        if (result.meta.requestStatus === 'fulfilled') {
          posted += 1;
        } else {
          failure = (result.payload as string) ?? 'Please try again.';
          break;
        }
      }

      dispatch(closeMessageModal());
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: failure ? 'WARN' : 'SUCCESS',
          title: failure ? 'Some payments posted' : 'Payments posted',
          subTitle: failure
            ? `${posted} of ${group.records.length} posted before stopping: ${failure}`
            : `${posted} payment(s) totalling ${formatCurrency(group.total)} posted.`,
        })
      );
    },
    [dispatch, supplierId, pendingAllocations]
  );

  const groupBands = (
    <div className="flex flex-col gap-2">
      {visibleGroups.map(({ group, visibleRecords }) => {
        /**
         * A band with no credit behind it is not a deposit, and the two kinds differ.
         *
         * `no-bank-feed` is a collection of independently postable payments. `no-linked-deposit`
         * should be empty: those records claim to be settled but name no credit, and the group
         * endpoint is keyed by credit, so there is nothing to post them against.
         */
        const isNoFeed = group.key === 'no-bank-feed';
        const isOrphaned = group.key === 'no-linked-deposit';

        /**
         * An unreviewed member blocks the whole band, and it is the first thing said.
         *
         * Approving is per credit, not per queue — the server posts every payment linked to this
         * deposit — so a payment sitting in Exceptions would go to the ERP as unapplied cash on
         * the strength of a button the operator pressed for the rows above it. Ordered ahead of
         * the shortfall reason because these payments are counted in `matchedAmount`, so the
         * credit can be fully accounted for and still not approvable.
         */
        const blocked =
          isOrphaned ||
          group.exceptions.length > 0 ||
          group.unaccounted > 0.005 ||
          group.unpostable.length > 0 ||
          group.unapprovedOpenCredit.length > 0;
        const blockedReason = isOrphaned
          ? 'These are marked settled but name no bank deposit, so there is nothing to date an ERP deposit from. Re-link them in Unmatched Deposits.'
          : group.exceptions.length > 0
          ? `${group.exceptions.length} payment(s) on this deposit are still in Exceptions. Approving posts the whole deposit, so allocate them in Exceptions — or unlink them — first.`
          : group.unaccounted > 0.005
          ? `${formatCurrency(
              group.unaccounted
            )} of this deposit is not linked to a payment yet. Finish linking it in Unmatched Deposits.`
          : group.unpostable.length === 0 && group.unapprovedOpenCredit.length > 0
          ? // Named rather than counted when it is one payment, because the fix is to open that
            // payment — and a collapsed band is the case this message exists for.
            `${formatCurrency(
              group.unapprovedOpenCredit.reduce(
                (sum, record) =>
                  sum + calculateUnappliedAmount(record.amount, allocationForPost(record)),
                0
              )
            )} across ${group.unapprovedOpenCredit.length} payment(s) — ${
              group.unapprovedOpenCredit[0]?.payerName ?? 'one of these'
            }${
              group.unapprovedOpenCredit.length > 1 ? ' and others' : ''
            } — is not allocated to any invoice. Open each one and either allocate the rest or tick "post the remainder as open credit".`
          : `Assign a customer to ${
              group.unpostable[0]?.payerName ?? 'a payment'
            } before approving this deposit.`;

        // The header counts every row in the band, unreviewed ones included — that is what the
        // credit brought in, and what an approval would post. `group.total` stays the reviewed
        // total: it is what the success message reports, and a band is only approvable when the
        // two are equal anyway.
        const exceptionTotal = group.exceptions.reduce((sum, record) => sum + record.amount, 0);
        const hiddenCount = group.records.length - visibleRecords.length;

        return (
          <DepositGroupBand
            key={group.key}
            deposit={group.deposit}
            title={group.title}
            paymentCount={group.records.length + group.exceptions.length}
            paymentTotal={group.total + exceptionTotal}
            unaccounted={group.unaccounted}
            /**
             * A search opens every band it leaves on screen, and takes the caret away while it
             * does.
             *
             * Collapsing is remembered per band and was being remembered through the search too:
             * the query filtered the rows correctly, then the closed band swallowed them, so
             * typing a payer who sat inside a band the operator had tidied away returned a header
             * and nothing else. Dropping `onToggle` rather than forcing `expanded` past it is what
             * `DepositGroupBand` documents for a band that cannot close, and it keeps the caret
             * from becoming a button that does nothing. The collapsed set is left untouched, so
             * clearing the search restores the bands the operator had shut.
             */
            expanded={isSearching || !collapsedKeys.has(group.key)}
            onToggle={isSearching ? undefined : () => toggleGroup(group.key)}
            action={
              <div title={blocked ? blockedReason : undefined}>
                <DillButton
                  onClick={() => (isNoFeed ? handleApproveEach(group) : handleApproveGroup(group))}
                  // A credit with one payment is still approved through the group route — the
                  // deposit is made for the credit either way — but calling it a group would be
                  // odd on screen. The no-feed band posts one receipt per payment, so it says how
                  // many rather than implying they become one thing.
                  label={
                    isNoFeed
                      ? `Approve ${group.records.length} payment${
                          group.records.length === 1 ? '' : 's'
                        }`
                      : group.records.length > 1
                      ? 'Approve group'
                      : 'Approve'
                  }
                  variant="primary"
                  isDisabled={blocked}
                  icon={<CheckCircle size={14} weight="bold" />}
                />
              </div>
            }>
            {/*
                Every field the table's columns carry, in the same order and at the same widths, so
                a payment reads identically whichever half of the queue it is in. Losing Created or
                Payment Date here would be a real loss: they are how an operator spots an advice
                that has been sitting around, or one whose dates disagree.

                The two exceptions are deliberate. Status/SettlementBadge is `defaultHidden` in the
                table and would be a constant here — everything in this queue is settled. And there
                is no per-member Approve: the deposit posts as one thing or not at all, so that
                button would be the one that produces a half-posted credit whose deposit can never
                be made.
              */}
            {visibleRecords.map((record) => {
              // `length > 0`, the same test `handleApproveGroup` applies: an empty pending set is
              // not sent as an override, so treating it as the allocation would report nothing
              // allocated for a record that posts its matcher suggestion.
              const pendingEdit = pendingAllocations[record.id];
              const pending = pendingEdit && pendingEdit.length > 0 ? pendingEdit : undefined;
              /**
               * What this row will post, and what it will leave on the customer's account.
               *
               * One derivation for the count, the total and the remainder, shared with the band's
               * gate — so a row that says it is waiting on a decision is a row that is actually
               * holding Approve up.
               */
              const postedAllocation = allocationForPost(record);
              const totalSuggested = postedAllocation.reduce(
                (sum, allocation) => sum + allocation.amountApplied,
                0
              );
              const openCredit = calculateUnappliedAmount(record.amount, postedAllocation);
              // Approving posts the remainder; approving without this is refused. The row has to
              // say which of the two it is, because one is a note and the other is the reason the
              // button above it does nothing.
              const openCreditApproved = record.openCreditApproved === true;

              return (
                <div key={record.id} className="flex items-center gap-3 px-3 py-2 pl-8">
                  <span className="w-28 text-xs text-gray-500 shrink-0">
                    {formatDateTime(record.createdAt)}
                  </span>
                  <span className="w-24 text-xs text-gray-700 shrink-0">
                    {formatDate(record.paymentDate)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-900 truncate">
                      {record.payerName ?? 'Unknown payer'}
                    </p>
                    {record.checkNumber && (
                      <p className="text-[11px] text-gray-400">Check #{record.checkNumber}</p>
                    )}
                    {!record.buyerId && (
                      <p className="text-[11px] text-amber-700">No customer assigned</p>
                    )}
                    {/* Beside the payer rather than in the invoice column, which is already at its
                        width and holds what *is* allocated. This is about the rest of the payment. */}
                    {openCredit > 0.005 && (
                      <p
                        className={
                          'text-[11px] flex items-center gap-1 ' +
                          (openCreditApproved ? 'text-amber-700' : 'text-red-600')
                        }
                        title={
                          openCreditApproved
                            ? `${formatCurrency(
                                openCredit
                              )} of this payment is not allocated to any invoice. It was approved to post as open credit on ${
                                record.buyerName ?? 'the customer'
                              }'s account.`
                            : `${formatCurrency(
                                openCredit
                              )} of this payment is not allocated to any invoice, and nobody has agreed to it posting as open credit — so this deposit cannot be approved. Open the payment and either allocate the rest or tick "post the remainder as open credit".`
                        }>
                        <Warning size={11} weight="fill" className="shrink-0" />
                        {openCreditApproved
                          ? `${formatCurrency(openCredit)} will post as open credit`
                          : `${formatCurrency(openCredit)} unallocated — needs sign-off`}
                      </p>
                    )}
                  </div>
                  <ChannelBadge channel={record.channel} />
                  <span className="w-24 text-xs font-semibold text-right text-gray-900 tabular-nums">
                    {formatCurrency(record.amount)}
                  </span>
                  <ConfidenceBadge
                    confidence={record.matchConfidence}
                    showLabel={false}
                    factors={record.matchConfidenceFactors ?? []}
                  />
                  <MatchedInvoicesTooltip invoices={record.matchedInvoices} pendingAllocs={pending}>
                    {({ onMouseEnter, onMouseLeave, ref }) => (
                      <div
                        ref={ref}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        className="flex items-center gap-1 w-52 cursor-pointer group">
                        <CaretDown
                          className="text-gray-400 group-hover:text-blue-500 transition-colors"
                          size={12}
                        />
                        <span className="text-[11px] text-gray-500 whitespace-nowrap">
                          {postedAllocation.length} invoice
                          {postedAllocation.length !== 1 ? 's' : ''} &middot;{' '}
                          {formatCurrency(totalSuggested)}
                        </span>
                        {pending && (
                          <span className="text-[11px] font-medium text-amber-700 bg-amber-100 rounded px-1">
                            Edited
                          </span>
                        )}
                      </div>
                    )}
                  </MatchedInvoicesTooltip>
                  <DillButton
                    onClick={() => openAdjustModal(record)}
                    variant="icon-blue"
                    icon={<PencilSimple size={14} />}
                  />
                  {/* Only where there is a link to break. A record in the no-bank-feed band has
                      no deposit, and one with a missing deposit has nothing to detach from. */}
                  {record.settlementBankRecordId && (
                    <DillButton
                      onClick={() => handleUnlink(record)}
                      variant="icon-red"
                      icon={<LinkBreak size={14} />}
                    />
                  )}
                  <DillButton
                    onClick={() => confirmDelete(record)}
                    variant="icon-red"
                    icon={<Trash size={14} />}
                  />
                </div>
              );
            })}

            {/*
                The credit's unreviewed payments, listed but not workable from here.

                Read-only on purpose: allocating belongs to Exceptions, and duplicating that editor
                in this band would be the second route to the ERP the Exceptions queue was
                deliberately not given. Unlink is the one action offered, because it is the answer
                when the payment does not belong on this deposit at all — and it is what releases
                the rest of the band to be approved.
              */}
            {group.exceptions.map((record) => (
              <div
                key={record.id}
                className="flex items-center gap-3 px-3 py-2 pl-8 bg-amber-50/60">
                <span className="w-28 text-xs text-gray-500 shrink-0">
                  {formatDateTime(record.createdAt)}
                </span>
                <span className="w-24 text-xs text-gray-700 shrink-0">
                  {formatDate(record.paymentDate)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-gray-900 truncate">
                    {record.payerName ?? 'Unknown payer'}
                  </p>
                  <p className="text-[11px] text-amber-700">
                    In Exceptions — not allocated yet. Allocate it there before approving this
                    deposit.
                  </p>
                </div>
                <ChannelBadge channel={record.channel} />
                <span className="w-24 text-xs font-semibold text-right text-gray-900 tabular-nums">
                  {formatCurrency(record.amount)}
                </span>
                <span className="text-[11px] font-medium text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">
                  Exception
                </span>
                {record.settlementBankRecordId && (
                  <DillButton
                    onClick={() => handleUnlink(record)}
                    variant="icon-red"
                    icon={<LinkBreak size={14} />}
                  />
                )}
              </div>
            ))}

            {/*
                The band's header counts the whole credit and Approve posts the whole credit, so a
                search that hides rows has to say so — otherwise the two disagree with what is on
                screen and the count looks wrong.
              */}
            {hiddenCount > 0 && (
              <p className="px-3 py-2 pl-8 text-[11px] italic text-gray-500">
                {hiddenCount} more payment(s) on this deposit are hidden by the search. Approving
                posts the whole deposit, these included.
              </p>
            )}
          </DepositGroupBand>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="flex flex-col min-h-0 gap-3">
        {/*
          The queue's own search, now that there is no table to own one.
          Same shape as DillTable's so the tab does not change character between sub-tabs.
        */}
        <div className="flex items-center justify-between gap-2">
          <div className="relative">
            <MagnifyingGlass
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search payer, check #..."
              className="h-8 pl-8 pr-3 text-xs border border-gray-200 rounded-lg bg-white text-gray-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-50 transition-all w-52"
            />
          </div>
          <span className="text-xs text-gray-400">
            <span className="font-medium text-gray-700">{items.length}</span> payment
            {items.length === 1 ? '' : 's'} in{' '}
            <span className="font-medium text-gray-700">{depositGroups.length}</span> deposit
            {depositGroups.length === 1 ? '' : 's'}
          </span>
        </div>

        {/*
          A tinted, scrolling well rather than a plain stack: the bands are cards, and without a
          surface behind them a long queue reads as a run of loose boxes on the page background.
        */}
        <div className="flex-1 min-h-0 overflow-y-auto rounded-xl bg-gray-50 border border-gray-100 p-3">
          {loading && items.length === 0 ? (
            <p className="py-16 text-xs text-center text-gray-400">Loading payments...</p>
          ) : visibleGroups.length === 0 ? (
            <p className="py-16 text-xs text-center text-gray-400">
              {items.length === 0
                ? 'All caught up — no payments are awaiting 1-click review.'
                : 'No payments match that search.'}
            </p>
          ) : (
            groupBands
          )}
        </div>
      </div>

      {/*
        Adjustment Modal.

        One payment of several on a credit gets no posting action, only Save — that credit's deposit
        posts as one thing, and posting a single member from here would strand the rest against a
        deposit that could not be made until they caught up. Omitting the handlers is the
        enforcement, which is how AdjustAllocationModal already expects to be told this (see its own
        comment); the saved allocation is picked up by Approve group.

        A credit with one payment keeps both actions, including Post without applying — that is the
        only route to unapplied cash from this queue, and it is safe there because the payment is
        the whole of the credit.
      */}
      <AdjustAllocationModal
        adjustItem={adjustItem}
        allocations={allocations}
        onAllocationChange={handleAllocationChange}
        totalAllocated={totalAllocated}
        filteredOpenInvoices={filteredOpenInvoices}
        invoiceSearch={invoiceSearch}
        onInvoiceSearchChange={setInvoiceSearch}
        onClose={closeAdjustModal}
        onSaveOnly={handleSaveOnly}
        onAdjustAndPost={adjustItemIsBulkMember ? undefined : handleAdjustAndPost}
        onPostWithoutApply={adjustItemIsBulkMember ? undefined : handlePostUnapplied}
        saveLabel={
          savingAllocation ? 'Saving...' : adjustItemIsBulkMember ? 'Save allocation' : undefined
        }
        isValidAllocation={!!isValidAllocation}
        hasAllocations={hasAllocations && !savingAllocation}
        onPostRemainderChange={setPostRemainderAsCredit}
        postRemainderAsCredit={postRemainderAsCredit}
        onAllocationsReset={() => setAllocations({})}
      />
    </>
  );
};
